import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import { AlertNotice, AlertMessage, Input, SimpleModal } from 'shared/components/';
import { withTranslation } from 'react-i18next';
import * as coreActions from 'core/duck';
import * as dashboardActions from 'features/dashboard/duck';
import { orderTerms } from 'features/dashboard/selectors';

import './TermsAndConditions.scss';

const customStyles = {
  overlay: {
    overflow: 'auto',
    alignItems: 'unset',
  },
  content: {
    margin: '3% 0',
    maxHeight: 'none',
  },
};

const mapTerms = (terms) => {
  const mapped = [];
  terms.forEach((t) => {
    mapped.push({
      ...t,
      checked: false,
    });
  });
  return mapped;
};

const newline2br = (text) => text.replace(/(?:\r\n|\r|\n)/g, '<br>');

class TermsAndConditions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      unacceptedTerms: mapTerms(props.userTerms),
      acceptedTerms: [],
      currentTerm: {
        ...props.userTerms[0],
        checked: false,
      },
      logOutConfirm: false,
      termsModalOpen: !!props.userTerms.length,
      alertMessage: '',
      showMessage: false,
      alertPosition: '',
    };

    this.acceptTerms = this.acceptTerms.bind(this);
    this.handleCheck = this.handleCheck.bind(this);
    this.logOutUser = this.logOutUser.bind(this);
    this.logOutUserConfirm = this.logOutUserConfirm.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
  }

  acceptTerms() {
    const { currentTerm } = this.state;
    const { currentUser } = this.props;

    if (currentTerm.checked) {
      this.props.actions.acceptTerms(this.props.user.id, currentTerm.id).then((result) => {
        if (!result?.status) {
          this._advanceTerm(currentTerm, currentUser);
          return;
        }
        // API error — re-fetch terms to verify if backend saved despite a timeout (e.g. 504)
        this.props.actions.getAvailableTerms(this.props.user.id, 'active').then((freshTerms) => {
          const termStillPending = !Array.isArray(freshTerms) || freshTerms.some((t) => t.id === currentTerm.id);
          if (!termStillPending) {
            this._advanceTerm(currentTerm, currentUser, true);
            return;
          }
          this.setState({
            error: 'errors.saveError',
            isError: false,
            showMessage: true,
            showMessageTime: 3000,
            alertMessage: 'dashboard.termsAndConditions.errorMessage',
          });
        });
      });
      return;
    }
    this.setState({
      error: this.props.t('testCenter.terms.agreementError', { name: currentTerm.name }),
    });
  }

  _advanceTerm(currentTerm, currentUser, termsAlreadyRefreshed = false) {
    const [, ...restTerms] = this.state.unacceptedTerms;

    if (!restTerms.length) {
      if (termsAlreadyRefreshed) {
        this.props.showTermsSuccessMessage();
      } else {
        this.props.actions.getAvailableTerms(this.props.user.id, 'active').then(() => {
          this.props.showTermsSuccessMessage();
        });
      }
      this.setState((prevState) => ({
        acceptedTerms: [...prevState.acceptedTerms, currentTerm],
        unacceptedTerms: restTerms,
        termsModalOpen: false,
      }));
      sessionStorage.setItem('notificationTooltip', true);
      return;
    }
    this.setState((prevState) => ({
      acceptedTerms: [...prevState.acceptedTerms, currentTerm],
      unacceptedTerms: restTerms,
      currentTerm: restTerms[0],
      error: '',
      isError: false,
      showMessage: true,
      showMessageTime: 3000,
      alertMessage: 'dashboard.termsAndConditions.successMessage',
      alertMessageParams: { email: currentUser.emailAddress },
    }));
  }

  handleCheck() {
    this.setState((prevState) => ({
      currentTerm: {
        ...prevState.currentTerm,
        checked: !prevState.currentTerm.checked,
      },
      error: '',
    }));
  }

  logOutUserConfirm(e) {
    if (e) {
      e.preventDefault();
    }
    this.setState((prevState) => ({
      logOutConfirm: !prevState.logOutConfirm,
    }));
  }

  logOutUser() {
    this.props.actions.logoutUser().then(() => {
      this.setState({
        logOutConfirm: false,
      });
    });
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  render() {
    const { userTerms } = this.props;
    const { currentTerm, error, acceptedTerms, termsModalOpen } = this.state;

    return (
      !!userTerms.length && (
        <div className="terms-conditions-container">
          <Modal
            isOpen={termsModalOpen}
            onRequestClose={() => {}}
            className="modal-info terms-and-conditions-modal"
            shouldCloseOnOverlayClick={false}
            ariaHideApp={false}
            style={customStyles}
            contentLabel="Modal">
            <div className="terms-modal-content">
              <AlertNotice message={this.props.t('dashboard.termsAndConditions.infoMessage')} />
              <div id="terms" className="mt-4">
                <h5>
                  {this.props.t('common.labels.countOf', {
                    some: acceptedTerms.length + 1,
                    total: userTerms.length,
                  })}
                </h5>
                <h4> {currentTerm.name} </h4>
                <div
                  className="mt-4 mb-4"
                  dangerouslySetInnerHTML={{ __html: newline2br(currentTerm.text || '') }}
                />
                <div className="section-bottom">
                  <Input
                    type="checkbox"
                    name="terms"
                    id="checkTerms"
                    value={currentTerm.checked}
                    onChange={this.handleCheck}
                    required>
                    <label htmlFor="checkTerms">
                      {this.props.t('dashboard.termsAndConditions.agreeLabel', {
                        name: currentTerm.name,
                      })}
                    </label>
                  </Input>
                  {!!error && <AlertNotice message={this.props.t(error)} type="error" />}
                  <button className="btn btn-action w-100 m-0" onClick={this.acceptTerms}>
                    {this.props.t('common.labels.continue')}
                  </button>
                  <div className="mt-4 w-100">
                    <button className="btn-link" onClick={this.logOutUserConfirm}>
                      {this.props.t('dashboard.termsAndConditions.logOut')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Modal>
          <SimpleModal
            isOpen={this.state.logOutConfirm}
            onCancel={this.logOutUserConfirm}
            onConfirm={this.logOutUser}
            confirmLabel={this.props.t('common.labels.yes')}
            cancelLabel={this.props.t('common.labels.no')}
            headline={this.props.t('profile.logOutQuestion')}
          />
          <div className={this.state.alertPosition}>
            <AlertMessage
              show={this.state.showMessage}
              time={this.state.showMessageTime}
              removeMessage={this.removeMessage}
              message={this.state.alertMessage}
              messageParams={this.state.alertMessageParams}
              isError={this.state.isError}
            />
          </div>
        </div>
      )
    );
  }
}

function mapStateToProps(state) {
  return {
    currentUser: state.users.currentUser,
    userTerms: orderTerms(state),
  };
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators({ ...dashboardActions, ...coreActions }, dispatch) };
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(TermsAndConditions));

TermsAndConditions.propTypes = {
  userTerms: PropTypes.arrayOf(PropTypes.shape({})),
  user: PropTypes.shape({}).isRequired,
  actions: PropTypes.shape({
    acceptTerms: PropTypes.func,
    getAvailableTerms: PropTypes.func,
    logoutUser: PropTypes.func,
  }).isRequired,
  t: PropTypes.func.isRequired,
};
